import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';

import { basicPipelineDashboardActions } from 'actions/modules/datascience/dashboard';
import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';

import { pipelinesQueries } from '../datascience';
import { dataStorageQueries } from '../datastorage';

import { useQueries, useQuery } from '@tanstack/react-query';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';

export default(() => {

  const openGroup = groupName => {
    const url = DashboardLinkFactory.dataLake.group({ groupName })
    CommonService.openInNewTab(url)
  }

  // A type's status search. Formula statuses only come from the component status search, which pages by token.
  const searchStatuses = (getQueryBuilder, { type, ...params }) => statusesActions.isFormulaType(type)
    ? {
      ...StatusesQuery.QUERIES.searchByComponent(getQueryBuilder, { ...params, type }),
      initialPageParam: undefined,
      getNextPageParam: CommonService.getNextPageParamToken
    }
    : {
      ...StatusesQuery.QUERIES.search(getQueryBuilder, { ...params, type }),
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam
    }

  // A formula's latest run: undefined when it has not run yet. `name` is the formula's name without `.json`.
  const getLatestFormulaRunQuery = ({ getQueryBuilder, groupName, name, enabled = true }) => ({
    ...searchStatuses(
      getQueryBuilder,
      {
        aggregate: true,
        groupName,
        name: `${name}.json`,
        size: 1,
        type: statusesActions.FORMULA_TYPE
      }
    ),
    enabled: enabled && !!groupName && !!name,
    select: response => response?.items?.[0]
  })

  const useGetLatestFormulaRun = ({ getQueryBuilder, groupName, name, shouldBeLimited, enabled = true }) =>
    useQuery({
      ...getLatestFormulaRunQuery({ getQueryBuilder, groupName, name, enabled }),
      refetchInterval: () => shouldBeLimited?.() ? undefined : 50_000
    })

  const toLatestRuns = results => ({
    data: results.map(({ data }) => data),
    isLoading: results.some(({ isLoading }) => isLoading)
  })

  const useGetLatestFormulaRuns = ({ getQueryBuilder, formulas, enabled = true, ...rest }) =>
    useQueries({
      queries: formulas.map(({ groupName, name }) => ({
        ...getLatestFormulaRunQuery({ getQueryBuilder, groupName, name, enabled }),
        ...rest
      })),
      combine: toLatestRuns
    })

  // `formula` ({ groupName, name, version }) opens that formula's run on the Monitor instead of the run's own item.
  const useOpenBasedOnStatusType = ({ jobId, param, type, formula = undefined }) => {
    const dispatch = useDispatch(),
          storageScreen = useSelector(state => state.myAccountState.settingsState.storageScreen),
          getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
          getQueryBuilder = useCallback(() => new Query.Builder(getApiConfigLocal()), [getApiConfigLocal]),
          { data: dataprep, refetch: getDataPrep } = dataStorageQueries.useGetDataPrepById({ getQueryBuilder, id: param, enabled: false }),
          { data: pipeline, refetch: getPipeline } = pipelinesQueries.useGetPipelineByIdQuery({ getQueryBuilder, pipelineId: param, enabled: false }),
          openDataPrep = useCallback(() => {
            (dataprep ? Promise.resolve({ data: dataprep }) : getDataPrep())
              .then(({ data: { outputGroup, name, version } }) => {
                const url = DashboardLinkFactory.dataStorage.dataPrep({ groupName: outputGroup, name, query: "", storageScreen, version })
                CommonService.openInNewTab(url)
              })
          }, [dataprep, getDataPrep, storageScreen]),
          openPipeline = useCallback(() => {
            (pipeline ? Promise.resolve({ data: pipeline }) : getPipeline())
              .then(({ data: { pipeline: { groupName, name, version } } } = { data: { pipeline: {} } }) => {
                const path = DashboardLinkFactory.dataScience.pipeline({ groupName, pipelineName: name, version, jobId })
                CommonService.openInNewTab(path)
              })
          }, [getPipeline, jobId, pipeline]),
          openGroupLocal = useCallback(() => openGroup(param), [param]),
          openFormula = useCallback(() => {
            const url = basicPipelineDashboardActions.getFormulaMonitorUrl({ groupName: formula.groupName, latestRun: { jobId, payload: { formulaVersion: formula.version } }, name: formula.name })
            CommonService.openInNewTab(url)
          }, [formula, jobId]),
          openBasedOnStatusType = useCallback(() => {
            if(formula) {
              return openFormula()
            }
            switch(type) {
              default:
              case StatusesEnums.STATUS_TYPE.PIPELINE:
                return openPipeline()
              case StatusesEnums.STATUS_TYPE.ETL:
                return openGroupLocal()
              case StatusesEnums.STATUS_TYPE.DATAPREP:
                return openDataPrep()
            }
          }, [formula, openDataPrep, openFormula, openGroupLocal, openPipeline, type])
    return openBasedOnStatusType;
  }

  return {
    searchStatuses,
    useGetLatestFormulaRun,
    useGetLatestFormulaRuns,
    useOpenBasedOnStatusType
  }
})()
